import { normalizeVec3 } from './math.js';
export class BattlefieldTerrain {
    // Flat arena center radius
    flatRadius = 60;
    /**
     * Evaluates terrain height at world coordinates (x, z).
     */
    getHeight(x, z) {
        const distFromCenter = Math.sqrt(x * x + z * z);
        // Central training grounds are smooth and flat
        if (distFromCenter < this.flatRadius) {
            const edgeBlend = Math.max(0, (distFromCenter - (this.flatRadius - 15)) / 15);
            const rawHeight = this.calculateRawHeight(x, z);
            return rawHeight * edgeBlend * edgeBlend;
        }
        return this.calculateRawHeight(x, z);
    }
    calculateRawHeight(x, z) {
        // Multi-octave natural terrain approximation
        const h1 = Math.sin(x * 0.02) * Math.cos(z * 0.02) * 5.0;
        const h2 = Math.sin((x + 40) * 0.05) * Math.sin((z - 30) * 0.04) * 2.5;
        const h3 = Math.cos(x * 0.08 + z * 0.06) * 1.2;
        // Add distinct tactical ridges on the flanks
        const ridge1 = Math.exp(-Math.pow((x - 110) / 25, 2)) * 8.0;
        const ridge2 = Math.exp(-Math.pow((z + 120) / 30, 2)) * 7.5;
        const crater1 = -Math.exp(-Math.pow((x - 45) / 10, 2) - Math.pow((z - 45) / 10, 2)) * 3.0;
        const crater2 = -Math.exp(-Math.pow((x + 60) / 12, 2) - Math.pow((z - 50) / 12, 2)) * 3.5;
        return h1 + h2 + h3 + ridge1 + ridge2 + crater1 + crater2;
    }
    /**
     * Calculates surface normal vector at (x, z) via finite differences.
     */
    getNormal(x, z) {
        const eps = 0.5;
        const hL = this.getHeight(x - eps, z);
        const hR = this.getHeight(x + eps, z);
        const hD = this.getHeight(x, z - eps);
        const hU = this.getHeight(x, z + eps);
        const normal = {
            x: (hL - hR) / (2 * eps),
            y: 1.0,
            z: (hD - hU) / (2 * eps),
        };
        return normalizeVec3(normal);
    }
    /**
     * Default tactical obstacles placed across the training battlefield
     */
    getStaticObstacles() {
        const rawObstacles = [
            // Central training ground barriers (dragon's teeth & concrete blocks)
            { id: 'barrier-1', type: 'barrier', position: { x: -25, y: 0, z: -20 }, radius: 3.5, width: 2.0, length: 7.0, height: 1.6, rotation: 0.3 },
            { id: 'barrier-2', type: 'barrier', position: { x: 25, y: 0, z: 20 }, radius: 3.5, width: 2.0, length: 7.0, height: 1.6, rotation: -0.4 },
            { id: 'barrier-3', type: 'barrier', position: { x: -30, y: 0, z: 25 }, radius: 3.5, width: 2.0, length: 7.0, height: 1.6, rotation: 1.2 },
            { id: 'barrier-4', type: 'barrier', position: { x: 30, y: 0, z: -25 }, radius: 3.5, width: 2.0, length: 7.0, height: 1.6, rotation: -1.1 },
            // Reinforced command bunker
            { id: 'bunker-north', type: 'bunker', position: { x: 0, y: 0, z: -85 }, radius: 7.0, width: 12.0, length: 12.0, height: 4.5, rotation: 0 },
            { id: 'bunker-south', type: 'bunker', position: { x: 0, y: 0, z: 85 }, radius: 7.0, width: 12.0, length: 12.0, height: 4.5, rotation: Math.PI },
            // Ruined warehouse / depot
            { id: 'building-east', type: 'building', position: { x: 90, y: 0, z: 10 }, radius: 8.5, width: 14.0, length: 18.0, height: 6.0, rotation: 0.2 },
            { id: 'building-west', type: 'building', position: { x: -90, y: 0, z: -10 }, radius: 8.5, width: 14.0, length: 18.0, height: 6.0, rotation: -0.2 },
            // Boulders & natural rock formations on ridge flanks
            { id: 'rock-1', type: 'rock', position: { x: 50, y: 0, z: -70 }, radius: 4.0, height: 3.5 },
            { id: 'rock-2', type: 'rock', position: { x: -60, y: 0, z: -65 }, radius: 4.5, height: 4.0 },
            { id: 'rock-3', type: 'rock', position: { x: 75, y: 0, z: 65 }, radius: 5.0, height: 4.2 },
            { id: 'rock-4', type: 'rock', position: { x: -70, y: 0, z: 75 }, radius: 4.0, height: 3.8 },
            // Wrecked tanks (cover)
            { id: 'wreck-1', type: 'tank_wreck', position: { x: -15, y: 0, z: -45 }, radius: 3.5, width: 3.5, length: 6.5, height: 2.4, rotation: 0.8 },
            { id: 'wreck-2', type: 'tank_wreck', position: { x: 18, y: 0, z: 45 }, radius: 3.5, width: 3.5, length: 6.5, height: 2.4, rotation: -0.9 },
        ];
        return rawObstacles.map(obs => ({
            ...obs,
            position: {
                x: obs.position.x,
                y: this.getHeight(obs.position.x, obs.position.z),
                z: obs.position.z,
            }
        }));
    }
}
//# sourceMappingURL=terrain.js.map